import { useEffect, useState } from 'react'
import { getShopOrderReceipt } from '../services/shop.service'
import type { ShopOrderReceiptResponse } from '@/lib/firebase/functions'

const POLL_INTERVAL_MS = 5 * 1000
const MAX_POLL_ATTEMPTS = 24

export function useOrderStatus(orderId: string | null, receiptToken: string | null) {
  const [order, setOrder] = useState<ShopOrderReceiptResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let disposed = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let pollAttempts = 0

    if (!orderId || !receiptToken) {
      setOrder(null)
      setError(false)
      setLoading(false)
      return () => undefined
    }

    setOrder(null)
    setError(false)
    setLoading(true)

    const receiptOrderId = orderId
    const receiptCapability = receiptToken

    async function loadReceipt() {
      try {
        const receipt = await getShopOrderReceipt(receiptOrderId, receiptCapability)
        if (disposed) return
        setOrder(receipt)
        setLoading(false)

        if (receipt.status === 'pending_payment' && pollAttempts < MAX_POLL_ATTEMPTS) {
          pollAttempts += 1
          timer = setTimeout(() => void loadReceipt(), POLL_INTERVAL_MS)
        }
      } catch {
        if (disposed) return
        setOrder(null)
        setError(true)
        setLoading(false)
      }
    }

    void loadReceipt()
    return () => {
      disposed = true
      if (timer) clearTimeout(timer)
    }
  }, [orderId, receiptToken])

  return { order, loading, error }
}
